import {
  AirlineChoice,
  CashBackBigPurchase,
  CashBackPreference,
  CreditScore,
  CurrentBalanceCompany,
  HotelChoice,
  HowLongToPayOffDebt,
  IntroAPROrRewards,
  LuxuryPerks,
  ManagingCategories,
  PrimaryGoal,
  RewardsAndPerks,
  SpecificAirlineOrHotelValues,
  SpendingCategories,
  TypeOfDebt,
  StudentSpendingCategories,
  DebtTypeEnum,
  PurchaseSpendingCategories,
} from 'src/types/inputs';

const InputBoolean = {
  True: true,
  False: false,
};

export enum Input {
  AirlineChoice = 'airlineChoice',
  AnnualFees = 'annualFees',
  CardDeposit = 'cardDeposit',
  CollegeStudent = 'collegeStudent',
  CreditScore = 'creditScore',
  CurrentBalanceCompany = 'currentBalanceCompany',
  FullDebt = 'fullDebt',
  HotelChoice = 'hotelChoice',
  HowLongToPayOffDebt = 'howLongToPayOffDebt',
  InternationalTravel = 'internationalTravel',
  IsBuildingCredit = 'isBuildingCredit',
  IsDebtCreditCard = 'isDebtCreditCard',
  MonthlySpend = 'monthlySpend',
  LuxuryPerks = 'luxuryPerks',
  PrimaryGoal = 'primaryGoal',
  RewardsAndPerks = 'rewardsAndPerks',
  SpecificAirlineOrHotel = 'specificAirlineOrHotel',
  SecurityDeposit = 'securityDeposit',
  IntroAPROrRewards = 'introAPROrRewards',
  PurchaseSpend = 'purchaseSpend',
  CashBackBigPurchase = 'cashBackBigPurchase',
  CashBackPreference = 'cashBackPreference',
  SpendingCategories = 'spendingCategories',
  ManagingCategories = 'managingCategories',
  TypeOfDebt = 'typeOfDebt',
  StudentSpendingCategories = 'studentSpendingCategories',
  PurchaseSpendingCategories = 'purchaseSpendingCategories',
}

export const Inputs = {
  [Input.AirlineChoice]: [AirlineChoice],
  [Input.AnnualFees]: InputBoolean,
  [Input.CardDeposit]: InputBoolean,
  [Input.CollegeStudent]: InputBoolean,
  [Input.CreditScore]: CreditScore,
  [Input.CurrentBalanceCompany]: CurrentBalanceCompany,
  [Input.FullDebt]: [
    {
      debtType: DebtTypeEnum,
      institution: CurrentBalanceCompany,
      amount: Number,
    },
  ],
  [Input.HotelChoice]: [HotelChoice],
  [Input.HowLongToPayOffDebt]: HowLongToPayOffDebt,
  [Input.InternationalTravel]: InputBoolean,
  [Input.IsBuildingCredit]: InputBoolean,
  [Input.IsDebtCreditCard]: InputBoolean,
  [Input.MonthlySpend]: Number,
  [Input.LuxuryPerks]: [LuxuryPerks],
  [Input.PrimaryGoal]: PrimaryGoal,
  [Input.RewardsAndPerks]: RewardsAndPerks,
  [Input.SpecificAirlineOrHotel]: SpecificAirlineOrHotelValues,
  [Input.SecurityDeposit]: Number,
  [Input.IntroAPROrRewards]: IntroAPROrRewards,
  [Input.PurchaseSpend]: Number,
  [Input.CashBackBigPurchase]: CashBackBigPurchase,
  [Input.CashBackPreference]: CashBackPreference,
  [Input.ManagingCategories]: ManagingCategories,
  [Input.SpendingCategories]: [SpendingCategories],
  [Input.TypeOfDebt]: TypeOfDebt,
  [Input.StudentSpendingCategories]: [StudentSpendingCategories],
  [Input.PurchaseSpendingCategories]: [PurchaseSpendingCategories],
};

export const ALGORITHM_VERSION = 2;

export const auth0Route = `${process.env.NEXT_PUBLIC_NW_URL}/api/nts/v1`;

export enum Routes {
  Results = '/pers-rec-results',
  Flow = '/recommend',
  Jump = 'card-finder-jump',
}

export enum IdentityProfiles {
  creditCardApplicationProfile = 'credit_card_application_profile',
  globalProfile = 'global_profile',
  tuCreditReportProfile = 'tu_credit_report_profile',
}

export enum Driver {
  AnnualFee,
  ApprovalOdds,
  BalanceTransferFee,
  BonusOffer,
  ForeignTransactionFee,
  IntroAPR,
  IntroOffer,
  MinimumDeposit,
  RegularAPR,
  RecommendedCreditScore,
  RewardsRate,
}

export enum LegacyPrimaryGoal {
  Travel = 'travel',
  CashBack = 'cashback',
  Debt = 'debt',
  BigPurchase = 'bigpurchase',
  BuildCredit = 'buildcredit',
}

export enum LegacyCreditScore {
  Excellent = 'excellent',
  Good = 'good',
  Average = 'average',
  Poor = 'poor',
  LimitedCreditHistory = 'limited_credit_history',
}

export enum LegacyCardDeposit {
  LessThan200 = 'lessThan200',
  Between200And300 = 'between200And300',
  MoreThan300 = 'moreThan300',
}

export enum LegacyHowLongToPayOffDebt {
  LessThan18Months = 'lessThan18Months',
  MoreThan18Months = 'moreThan18Months',
}

export enum LegacyCurrentBalanceCompany {
  BankOfAmerica = 'bankOfAmerica',
  CapitalOne = 'capitalOne',
  Chase = 'chase',
  Citi = 'citi',
  Discover = 'discover',
  WellsFargo = 'wellsFargo',
  UsBank = 'usBank',
  Other = 'other',
}

export enum LegacyAirlineChoice {
  AerLingus = 'aerLingus',
  Aeroplan = 'aeroplan',
  AmericanAirlines = 'american',
  AlaskaAirlines = 'alaska',
  BritishAirways = 'britishAirways',
  Delta = 'delta',
  Jetblue = 'jetblue',
  Iberia = 'iberia',
  SouthwestAirlines = 'southwest',
  UnitedAirlines = 'united',
  Other = 'other',
}

export enum LegacyHotelChoice {
  Hilton = 'hilton',
  Hyatt = 'hyatt',
  IHG = 'ihg',
  Marriot = 'marriot',
  Radisson = 'radisson',
  Wyndham = 'wyndham',
  Other = 'other',
}

export enum Strengths {
  Weak = 'Weak',
  WeakPlus = 'WeakPlus',
  Average = 'Average',
  AveragePlus = 'AveragePlus',
  Intermediate = 'Intermediate',
  IntermediatePlus = 'IntermediatePlus',
  Strong = 'Strong',
  StrongPlus = 'StrongPlus',
  Strongest = 'Strongest',
}
